/**
 * App root.
 *
 * Sets up routing and authentication context.
 * All authenticated routes are wrapped in ProtectedRoute.
 *
 * Route map:
 *   /                           → LandingPage (public)
 *   /dashboard                  → DashboardPage
 *   /sites                      → SitesPage
 *   /sites/new                  → SitesPage (create modal pre-opened)
 *   /sites/:id                  → SitePage
 *   /sites/:id/compliance       → ComplianceReportPage
 *   /scans/:id                  → ScanDetailPage
 *   /login                      → LoginPage
 *   /forgot-password            → ForgotPasswordPage
 *   /reset-password             → ResetPasswordPage
 *   /register                   → RegisterPage
 *   /platform-admin             → PlatformAdminPage (public — manages its own session)
 *   /billing                    → BillingPage (protected)
 *   /pricing                    → PricingPage (public)
 *   /resources                  → ResourcesPage (public)
 *   /methodology                → MethodologyPage (public)
 *   /sample-report              → SampleReportPage (public)
 *   /how-do-i-make-my-website-ada-compliant → AdaComplianceGuidePage (public)
 *   /how-to-check-wcag-compliance → WcagComplianceGuidePage (public)
 *   /can-ai-detect-accessibility-issues → AiAccessibilityGuidePage (public)
 *   /accessibility-audit-software-for-agencies → AgencyAuditSoftwareGuidePage (public)
 *   /best-accessibility-audit-tools → BestAccessibilityAuditToolsGuidePage (public)
 *   /how-to-prepare-for-eaa-compliance → EaaComplianceGuidePage (public)
 *   /accessibility-compliance-report-for-procurement → ProcurementReportGuidePage (public)
 *   /security                   → SecurityPage (public)
 *   /accessibility-statement    → AccessibilityStatementPage (public)
 *
 * IMPORTANT: /sites/new must be declared BEFORE /sites/:id so React Router
 * does not capture the literal string "new" as a UUID site param.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './lib/auth-context';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { PartnerRoute } from './components/layout/PartnerRoute';
import AppShellLayout from './components/layout/AppShellLayout';
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WorkspaceSelectionPage from './pages/WorkspaceSelectionPage';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import ResourcesPage from './pages/ResourcesPage';
import MethodologyPage from './pages/MethodologyPage';
import SampleReportPage from './pages/SampleReportPage';
import AdaComplianceGuidePage from './pages/AdaComplianceGuidePage';
import WcagComplianceGuidePage from './pages/WcagComplianceGuidePage';
import AiAccessibilityGuidePage from './pages/AiAccessibilityGuidePage';
import AgencyAuditSoftwareGuidePage from './pages/AgencyAuditSoftwareGuidePage';
import BestAccessibilityAuditToolsGuidePage from './pages/BestAccessibilityAuditToolsGuidePage';
import EaaComplianceGuidePage from './pages/EaaComplianceGuidePage';
import ProcurementReportGuidePage from './pages/ProcurementReportGuidePage';
import SecurityPage from './pages/SecurityPage';
import AccessibilityStatementPage from './pages/AccessibilityStatementPage';
import ContactPage from './pages/ContactPage';
import DashboardPage from './pages/DashboardPage';
import SitesPage from './pages/SitesPage';
import SitePage from './pages/SitePage';
import ScanDetailPage from './pages/ScanDetailPage';
import ComplianceReportPage from './pages/ComplianceReportPage';
import AlertsPage from './pages/AlertsPage';
import BillingPage from './pages/BillingPage';
import PricingPage from './pages/PricingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import WalkthroughPage from './pages/WalkthroughPage';
import PlatformAdminPage from './pages/PlatformAdminPage';
import WorkspaceSettingsPage from './pages/WorkspaceSettingsPage';
import PartnersPage from './pages/PartnersPage';
import PartnerApplyPage from './pages/PartnerApplyPage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import PartnerReferralsPage from './pages/PartnerReferralsPage';
import PartnerCommissionsPage from './pages/PartnerCommissionsPage';
import PartnerPayoutsPage from './pages/PartnerPayoutsPage';
import PartnerSettingsPage from './pages/PartnerSettingsPage';

function UnknownRouteRedirect() {
  const { isAuthenticated, hasPendingVerification, hasPendingWorkspaceSelection } = useAuth();
  return (
    <Navigate
      to={
        isAuthenticated
          ? '/dashboard'
          : hasPendingVerification
            ? '/verify-email'
            : hasPendingWorkspaceSelection
              ? '/select-workspace'
              : '/'
      }
      replace
    />
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/select-workspace" element={<WorkspaceSelectionPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/sample-report" element={<SampleReportPage />} />
          <Route path="/how-do-i-make-my-website-ada-compliant" element={<AdaComplianceGuidePage />} />
          <Route path="/how-to-check-wcag-compliance" element={<WcagComplianceGuidePage />} />
          <Route path="/can-ai-detect-accessibility-issues" element={<AiAccessibilityGuidePage />} />
          <Route path="/accessibility-audit-software-for-agencies" element={<AgencyAuditSoftwareGuidePage />} />
          <Route path="/best-accessibility-audit-tools" element={<BestAccessibilityAuditToolsGuidePage />} />
          <Route path="/how-to-prepare-for-eaa-compliance" element={<EaaComplianceGuidePage />} />
          <Route path="/accessibility-compliance-report-for-procurement" element={<ProcurementReportGuidePage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatementPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/walkthrough" element={<WalkthroughPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/partners/apply" element={<PartnerApplyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />

          {/* Protected routes (all authenticated roles) */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShellLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/sites" element={<SitesPage />} />
              {/* /sites/new must come before /sites/:id — "new" is not a UUID */}
              <Route path="/sites/new" element={<SitesPage autoOpenCreate />} />
              <Route path="/sites/:id" element={<SitePage />} />
              <Route path="/sites/:id/compliance" element={<ComplianceReportPage />} />
              <Route path="/scans/:id" element={<ScanDetailPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/workspace" element={<WorkspaceSettingsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route element={<PartnerRoute />}>
                <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />
                <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
                <Route path="/partner/referrals" element={<PartnerReferralsPage />} />
                <Route path="/partner/commissions" element={<PartnerCommissionsPage />} />
                <Route path="/partner/payouts" element={<PartnerPayoutsPage />} />
                <Route path="/partner/settings" element={<PartnerSettingsPage />} />
              </Route>
            </Route>
          </Route>

          {/* Public marketing routes */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Platform admin — public route, manages its own session via X-PA-Token */}
          <Route path="/platform-admin" element={<PlatformAdminPage />} />

          {/* Default redirect */}
          <Route path="*" element={<UnknownRouteRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
